/**
 * GSTR-2B Reconciliation
 *
 * Upload the GST portal's GSTR-2B JSON for a return period, then compare every
 * ITC-claimed bill in our books against it. Four buckets:
 *
 *   ✅  Matched                 — safe
 *   ⚠  Mismatched amount       — supplier / our books have wrong number
 *   ❌  Claimed, not on 2B      — MUST reverse or your ITC gets disallowed
 *   🔵  On 2B, not booked       — potential missing entry from books
 *
 * One-click "Reverse ITC" on any ❌ row unclaims the ITC (reverses the
 * original journal and re-posts gross to the expense head).
 */
import { useEffect, useState } from "react";
import { api, formatApiError, inr } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import { Upload, FileArrowUp, Warning, XCircle, CheckCircle, Info, ArrowClockwise } from "@phosphor-icons/react";

const PERIOD_LABEL = (p) => {
    if (!p || p.length !== 6) return p || "";
    const mm = p.slice(0, 2);
    const yyyy = p.slice(2);
    return `${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][parseInt(mm, 10) - 1] || mm} ${yyyy}`;
};

const MODULE_LABEL = { purchase: "Purchase", petty_cash: "Petty cash", expense_claim: "Expense claim" };

export default function Gstr2bRecon() {
    const [uploads, setUploads] = useState([]);
    const [selected, setSelected] = useState("");
    const [reco, setReco] = useState(null);
    const [tab, setTab] = useState("claimed_not_in_2b");
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [reversing, setReversing] = useState(null);

    const loadUploads = async () => {
        try {
            const { data } = await api.get("/gstr-2b/uploads");
            setUploads(data);
            if (data.length && !selected) setSelected(data[0].return_period);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { loadUploads(); }, []);

    const runReco = async (period) => {
        if (!period) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/gstr-2b/${period}/reconcile`);
            setReco(data);
        } catch (e) { toast.error(formatApiError(e)); setReco(null); }
        finally { setLoading(false); }
    };
    useEffect(() => { runReco(selected); }, [selected]);

    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData(); fd.append("file", file);
            const { data } = await api.post("/gstr-2b/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Uploaded 2B for ${PERIOD_LABEL(data.return_period)} — ${data.line_count} lines`);
            await loadUploads();
            setSelected(data.return_period);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setUploading(false); }
    };

    const reverseItc = async (row) => {
        if (!window.confirm(`Reverse ITC on ${MODULE_LABEL[row.module]} for supplier ${row.supplier_name || row.supplier_gstin}?\n\nThe original journal will be reversed and the bill re-posted gross (GST folded into the expense head). This is auditable and cannot be undone.`)) return;
        setReversing(row.id);
        try {
            await api.post(`/gstr-2b/reverse-itc/${row.module}/${row.id}`,
                            { reason: "Not present on GSTR-2B — reversed via 2B recon" });
            toast.success(`ITC reversed on ${row.invoice_no || row.id.slice(0, 8)}`);
            await runReco(selected);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setReversing(null); }
    };

    const s = reco?.summary;
    const buckets = {
        matched: {
            label: "Matched", icon: CheckCircle, colour: "#059669",
            rows: reco?.matched || [], count: s?.matched_count || 0,
        },
        mismatched: {
            label: "Amount mismatch", icon: Warning, colour: "#d97706",
            rows: reco?.mismatched || [], count: s?.mismatched_count || 0,
        },
        claimed_not_in_2b: {
            label: "Claimed, not on 2B", icon: XCircle, colour: "#dc2626",
            rows: reco?.claimed_not_in_2b || [], count: s?.claimed_not_in_2b_count || 0,
        },
        in_2b_not_booked: {
            label: "On 2B, not booked", icon: Info, colour: "#0ea5e9",
            rows: reco?.in_2b_not_booked || [], count: s?.in_2b_not_booked_count || 0,
        },
    };

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title="GSTR-2B Reconciliation"
                sub="Upload the GST portal's 2B JSON and reconcile every ITC-claimed bill in your books against what your suppliers actually filed."
            />
            <div className="p-8 space-y-6">
                {/* Upload strip */}
                <div className="bg-white border rounded-md p-5"
                     style={{ borderColor: "var(--se-border)" }}
                     data-testid="gstr2b-upload-card">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-[320px]">
                            <div className="font-serif text-lg">Upload GSTR-2B (JSON)</div>
                            <div className="text-xs text-stone-500 mt-1">
                                Download from GST portal → Services → Returns → GSTR-2B → JSON.
                                Re-uploading the same period replaces the previous copy.
                            </div>
                        </div>
                        <label className="cursor-pointer">
                            <input type="file" accept=".json,application/json" className="hidden"
                                   disabled={uploading}
                                   onChange={(e) => { const f = e.target.files?.[0]; upload(f); e.target.value = ""; }}
                                   data-testid="gstr2b-upload-input" />
                            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium"
                                  style={{ background: "#7c3aed", color: "#fff",
                                            opacity: uploading ? 0.7 : 1 }}>
                                <FileArrowUp size={16} />
                                {uploading ? "Uploading…" : "Upload 2B JSON"}
                            </span>
                        </label>
                    </div>
                </div>

                {/* Period selector + summary */}
                {uploads.length > 0 ? (
                    <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-xs uppercase tracking-widest text-stone-500">Return period</span>
                        <select value={selected} onChange={(e) => setSelected(e.target.value)}
                                className="border rounded-sm px-3 py-2 text-sm"
                                data-testid="gstr2b-period-select">
                            {uploads.map((u) => (
                                <option key={u.id} value={u.return_period}>
                                    {PERIOD_LABEL(u.return_period)} · uploaded {u.uploaded_at.slice(0, 10)} · {u.line_count} lines
                                </option>
                            ))}
                        </select>
                        <Button variant="outline" size="sm" onClick={() => runReco(selected)}
                                disabled={loading} data-testid="gstr2b-refresh">
                            <ArrowClockwise size={14} className="mr-1" />
                            {loading ? "Reconciling…" : "Re-run recon"}
                        </Button>
                    </div>
                ) : null}

                {/* Summary tiles */}
                {s ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <SummaryTile label="ITC claimed in books"
                                     value={s.our_itc_claimed}
                                     hint="Sum of Input CGST+SGST+IGST on all ITC-confirmed bills this period."
                                     testid="gstr2b-tile-claimed" />
                        <SummaryTile label="ITC available on 2B"
                                     value={s.twob_itc_available}
                                     hint="What your suppliers actually filed."
                                     testid="gstr2b-tile-available" />
                        <SummaryTile label="Delta (claimed − available)"
                                     value={s.our_itc_claimed - s.twob_itc_available}
                                     hint="If positive: at-risk ITC. If negative: unclaimed ITC on 2B."
                                     danger={s.our_itc_claimed - s.twob_itc_available > 0}
                                     testid="gstr2b-tile-delta" />
                        <SummaryTile label="At-risk ITC value"
                                     value={s.claimed_not_in_2b_value}
                                     hint="Total GST claimed on bills that have no match on 2B — reverse before the 3-month deadline."
                                     danger={s.claimed_not_in_2b_value > 0}
                                     testid="gstr2b-tile-at-risk" />
                    </div>
                ) : null}

                {/* Tabs */}
                {reco ? (
                    <div className="border-b flex gap-1" style={{ borderColor: "var(--se-border)" }}>
                        {Object.entries(buckets).map(([key, b]) => (
                            <button key={key}
                                    onClick={() => setTab(key)}
                                    data-testid={`gstr2b-tab-${key}`}
                                    className="px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors"
                                    style={{
                                        borderColor: tab === key ? b.colour : "transparent",
                                        color: tab === key ? b.colour : "#57534e",
                                    }}>
                                <b.icon size={14} className="inline mr-1" weight="fill" />
                                {b.label} <span className="ml-1 text-xs opacity-70">({b.count})</span>
                            </button>
                        ))}
                    </div>
                ) : null}

                {/* Table for current tab */}
                {reco ? <ReconTable tab={tab} bucket={buckets[tab]} reverseItc={reverseItc} reversing={reversing} /> : null}

                {uploads.length === 0 && !uploading ? (
                    <div className="text-sm text-stone-500 text-center py-16 border rounded-md bg-white"
                         style={{ borderColor: "var(--se-border)" }}>
                        No 2B uploaded yet. Drop the JSON above to get started.
                    </div>
                ) : null}
            </div>
        </>
    );
}


function SummaryTile({ label, value, hint, danger, testid }) {
    return (
        <div className="bg-white border rounded-md p-4"
             style={{ borderColor: danger ? "#fecaca" : "var(--se-border)",
                       background: danger ? "#fef2f2" : "#fff" }}
             data-testid={testid}>
            <div className="text-[11px] uppercase tracking-widest text-stone-500">{label}</div>
            <div className="text-2xl font-serif mt-1"
                 style={{ color: danger ? "#b91c1c" : "#292524" }}>
                ₹ {inr(Math.abs(value || 0))}{(value || 0) < 0 ? " ↓" : ""}
            </div>
            <div className="text-[10px] text-stone-500 mt-1">{hint}</div>
        </div>
    );
}


function ReconTable({ tab, bucket, reverseItc, reversing }) {
    if (!bucket.rows.length) {
        return (
            <div className="text-sm text-stone-500 text-center py-12 border rounded-md bg-white"
                 style={{ borderColor: "var(--se-border)" }}
                 data-testid="gstr2b-empty">
                Nothing in this bucket.
            </div>
        );
    }
    const showActions = tab === "claimed_not_in_2b" || tab === "mismatched";
    return (
        <div className="border rounded-md overflow-hidden bg-white"
             style={{ borderColor: "var(--se-border)" }}>
            <table className="se-table text-sm w-full" data-testid={`gstr2b-table-${tab}`}>
                <thead>
                    <tr>
                        <th>Module</th>
                        <th>Supplier</th>
                        <th>Invoice #</th>
                        <th>Date</th>
                        <th className="text-right">Taxable</th>
                        <th className="text-right">GST</th>
                        <th className="text-right">Total</th>
                        {tab === "matched" || tab === "mismatched"
                            ? <th className="text-right">2B GST</th> : null}
                        {tab === "mismatched" ? <th className="text-right">Δ Total</th> : null}
                        {showActions ? <th></th> : null}
                    </tr>
                </thead>
                <tbody>
                    {bucket.rows.map((r) => (
                        <tr key={`${r.module || "2b"}-${r.id || r.supplier_gstin + r.invoice_no}`}
                            data-testid={`gstr2b-row-${r.id || r.invoice_no}`}>
                            <td className="text-xs">
                                {r.module ? MODULE_LABEL[r.module] : <span className="text-sky-700">On 2B only</span>}
                            </td>
                            <td>
                                <div className="text-xs">{r.supplier_name || "—"}</div>
                                <div className="text-[10px] text-stone-500 font-mono">{r.supplier_gstin}</div>
                            </td>
                            <td className="font-mono text-xs">{r.invoice_no || "—"}</td>
                            <td className="text-xs">{(r.invoice_date || "").slice(0, 10)}</td>
                            <td className="se-num text-right">₹ {inr(r.taxable || 0)}</td>
                            <td className="se-num text-right">₹ {inr(r.gst_total || 0)}</td>
                            <td className="se-num text-right">₹ {inr(r.total || 0)}</td>
                            {tab === "matched" || tab === "mismatched"
                                ? <td className="se-num text-right">₹ {inr(r.twob?.gst_total || 0)}</td>
                                : null}
                            {tab === "mismatched"
                                ? <td className="se-num text-right"
                                       style={{ color: (r.delta_total || 0) > 0 ? "#b91c1c" : "#0f766e" }}>
                                    {r.delta_total > 0 ? "+" : ""}₹ {inr(Math.abs(r.delta_total || 0))}
                                  </td>
                                : null}
                            {showActions ? (
                                <td className="text-right">
                                    {(r.module === "petty_cash" || r.module === "purchase") ? (
                                        <Button size="sm" variant="outline"
                                                onClick={() => reverseItc(r)}
                                                disabled={reversing === r.id}
                                                style={{ color: "#b91c1c", borderColor: "#fecaca" }}
                                                data-testid={`gstr2b-reverse-${r.id}`}>
                                            {reversing === r.id ? "Reversing…" : "Reverse ITC"}
                                        </Button>
                                    ) : (
                                        <span className="text-[10px] text-stone-400">Reverse manually</span>
                                    )}
                                </td>
                            ) : null}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
